// Create a scoped game link for explicit manual sharing. No address book or
// private-group membership is needed and no email is sent.

import { api, ApiError } from "../api.ts";
import { esc, h, setMsg } from "../dom.ts";
import { t } from "../i18n/index.ts";
import { navigate } from "../router.ts";
import { currentUser } from "../session.ts";
import { topNav } from "../ui/topnav.ts";
import { quickChallenge } from "../ui/quickChallenge.ts";

export function inviteScreen(): HTMLElement {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  const el = h(`
    <main class="card wide">
      <section class="panel">
        <h2>${t("direct.title")}</h2>
        <p class="tagline">${t("direct.body")}</p>
        <form id="invite" novalidate>
          <div class="quick-slot"></div>
          <button class="btn primary" type="submit">${t("direct.create")}</button>
          <p class="msg" role="status" aria-live="polite"></p>
        </form>
        <div class="invite-result" hidden></div>
        <p class="fine"><a data-link href="/players">${t("invite.orMember")}</a></p>
      </section>
    </main>
  `);
  el.prepend(topNav(null));

  const qc = quickChallenge();
  el.querySelector<HTMLElement>(".quick-slot")!.append(qc.element);

  const form = el.querySelector<HTMLFormElement>("#invite")!;
  const msg = el.querySelector("#invite .msg") as HTMLElement;
  const result = el.querySelector(".invite-result") as HTMLElement;
  const button = form.querySelector("button") as HTMLButtonElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const { tc, color, rated } = qc.read();
    button.disabled = true;
    setMsg(msg, t("common.sending"));
    try {
      const res = await api.createGameLink(tc, color, rated);
      // UC-5: drop the inviter on the waiting-room board. The challenge is saved
      // server-side, so this is safe to leave; the room carries the link too.
      const token = res.invite_url.split("/i/")[1];
      if (token) {
        navigate(`/wait/${token}`);
        return;
      }
      // Fallback (shouldn't happen): show the link inline.
      setMsg(msg, "");
      result.hidden = false;
      result.innerHTML = `
        <p class="ok-line">${t("direct.ready")}</p>
        <p class="fine">${t("invite.shareLink")}</p>
        <input class="copy" readonly value="${esc(res.invite_url)}" />`;
      const copy = result.querySelector<HTMLInputElement>(".copy")!;
      copy.addEventListener("focus", () => copy.select());
      form.reset();
      button.disabled = false;
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("invite.failed"), "bad");
      button.disabled = false;
    }
  });

  return el;
}
